import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';

import { Option, CustomFieldConfig } from '../types';

import classes from './leadsWithReferrals.module.css';

interface ContactWithReferral {
  contactId: string;
  contactName: string;
  companyName: string | null;
  referredById: string;
  referredBy: string;
  referredByCompany: string;
  email: string | null;
  phone: string | null;
  createdAt: string;
}

interface LeadsWithReferralsProps {
  locationId: string;
  ssoToken: string;
  referralPartnerOptions: Option[];
  customFieldConfig: CustomFieldConfig;
  isLoadingContacts: boolean;
}

function LeadsWithReferralsComponent({
  locationId,
  ssoToken,
  referralPartnerOptions,
  // customFieldConfig not needed - we update contact.referred_by_id directly
  isLoadingContacts,
}: LeadsWithReferralsProps) {
  const [contacts, setContacts] = useState<ContactWithReferral[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editSearchTerm, setEditSearchTerm] = useState('');
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());

  const loadContactsWithReferrals = useCallback(async () => {
    if (!locationId || !ssoToken) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch contacts with referrals from the backend
      const response = await Api.get('/ghl/embed/contacts', {
        params: {
          locationId,
          with_referral: true,
          type: 'lead',
          limit: 500,
        },
        headers: { 'X-GHL-SSO': ssoToken },
      });

      // Handle response format
      const contactsData = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];

      // Map to display format
      const mappedContacts: ContactWithReferral[] = contactsData.map((contact: any) => {
        const firstName = contact.first_name || contact.firstName || '';
        const lastName = contact.last_name || contact.lastName || '';
        const contactName = [firstName, lastName].filter((n) => n.trim()).join(' ').trim()
          || contact.display_name
          || contact.displayName
          || '(No Name)';

        // Get referral partner info from contact.referred_by
        const referredBy = contact.referred_by || contact.referredBy || {};
        const refFirstName = referredBy.first_name || referredBy.firstName || '';
        const refLastName = referredBy.last_name || referredBy.lastName || '';
        const referredByName = [refFirstName, refLastName].filter((n) => n.trim()).join(' ').trim()
          || referredBy.display_name
          || referredBy.displayName
          || '(Unknown)';
        const referredByCompany = referredBy.company_name || referredBy.companyName || '';
        const referredById = referredBy.id || '';

        return {
          contactId: contact.id,
          contactName,
          companyName: contact.company_name || contact.companyName || null,
          referredById,
          referredBy: referredByName,
          referredByCompany,
          email: contact.email || null,
          phone: contact.phone || null,
          createdAt: contact.date_added || contact.dateAdded || contact.created_at || '',
        };
      });

      // Sort by created date descending
      mappedContacts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      setContacts(mappedContacts);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error loading contacts with referrals:', err);
      setError('Failed to load contacts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [locationId, ssoToken]);

  useEffect(() => {
    loadContactsWithReferrals();
  }, [loadContactsWithReferrals]);

  // Handle referral partner change
  const handleReferralPartnerChange = useCallback(async (contactId: string, newPartner: Option) => {
    setSavingIds((prev) => new Set(prev).add(contactId));
    setEditingId(null);
    setEditSearchTerm('');

    try {
      // Update the contact's referred_by_id directly
      await Api.put(`/ghl/embed/contacts/${contactId}`, {
        referred_by_id: newPartner.id,
      }, {
        headers: { 'X-GHL-SSO': ssoToken },
      });

      // Also update any opportunities for this contact
      try {
        const oppResponse = await Api.get('/ghl/embed/opportunities', {
          params: {
            'filter[contact_id]': contactId,
            limit: 100,
          },
          headers: { 'X-GHL-SSO': ssoToken },
        });

        const opportunities = Array.isArray(oppResponse?.data?.data)
          ? oppResponse.data.data
          : [];

        // Update each opportunity with the new referral partner
        await Promise.all(
          opportunities.map((opp: any) =>
            Api.put(`/ghl/embed/opportunities/${opp.id}`, {
              referral_partner_id: newPartner.id,
            }, {
              headers: { 'X-GHL-SSO': ssoToken },
            })
          )
        );
      } catch (oppErr) {
        // Log but don't fail the whole operation if opportunity update fails
        // eslint-disable-next-line no-console
        console.error('Failed to update opportunities:', oppErr);
      }

      // Update local state
      setContacts((prevContacts) =>
        prevContacts.map((contact) =>
          contact.contactId === contactId
            ? {
                ...contact,
                referredById: newPartner.id,
                referredBy: newPartner.label,
                referredByCompany: newPartner.companyName || '',
              }
            : contact
        )
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to update referral partner:', err);
      setError('Failed to update referral partner. Please try again.');
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(contactId);
        return next;
      });
    }
  }, [ssoToken]);

  // Filter referral partner options for edit dropdown
  const filteredEditOptions = useMemo(() => {
    if (!editSearchTerm) return referralPartnerOptions.slice(0, 10);
    const searchLower = editSearchTerm.toLowerCase();
    return referralPartnerOptions
      .filter(
        (option) =>
          option.label.toLowerCase().includes(searchLower)
          || (option.companyName && option.companyName.toLowerCase().includes(searchLower))
      )
      .slice(0, 10);
  }, [referralPartnerOptions, editSearchTerm]);

  const filteredContacts = useMemo(() => {
    if (!searchTerm) return contacts;
    const searchLower = searchTerm.toLowerCase();
    return contacts.filter(
      (contact) =>
        contact.contactName.toLowerCase().includes(searchLower)
        || (contact.companyName && contact.companyName.toLowerCase().includes(searchLower))
        || contact.referredBy.toLowerCase().includes(searchLower)
        || contact.referredByCompany.toLowerCase().includes(searchLower)
        || (contact.email && contact.email.toLowerCase().includes(searchLower))
        || (contact.phone && contact.phone.includes(searchTerm))
    );
  }, [contacts, searchTerm]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return '-';
    }
  };

  const handleEditClick = (contactId: string) => {
    setEditingId(contactId);
    setEditSearchTerm('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditSearchTerm('');
  };

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading contacts with referrals...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger alert-dismissible" role="alert">
        {error}
        <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close" />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <h5 className="mb-3">Contacts With Referral Partners</h5>

      <div className="mb-3">
        <p className="text-muted mb-2">
          Total contacts with referral partner: <strong>{contacts.length}</strong>
        </p>
        <input
          type="text"
          className="form-control"
          placeholder="Search by name, referral partner, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '400px' }}
        />
      </div>

      {filteredContacts.length === 0 ? (
        <div className="alert alert-info">
          {searchTerm
            ? 'No contacts found matching your search.'
            : 'No contacts with referral partners found.'}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead className="table-light">
              <tr>
                <th>Contact Name</th>
                <th style={{ minWidth: '200px' }}>Referred By</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Created Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map((contact) => (
                <tr key={contact.contactId}>
                  <td>{contact.contactName}</td>
                  <td>
                    {editingId === contact.contactId ? (
                      <div className={classes.editDropdown}>
                        <input
                          type="text"
                          className="form-control form-control-sm mb-1"
                          placeholder="Search referral partners..."
                          value={editSearchTerm}
                          onChange={(e) => setEditSearchTerm(e.target.value)}
                          autoFocus
                        />
                        {isLoadingContacts ? (
                          <div className="text-muted small">Loading...</div>
                        ) : (
                          <ul className={classes.dropdownList}>
                            {filteredEditOptions.map((option) => (
                              <li key={option.id}>
                                <button
                                  type="button"
                                  className={classes.dropdownItem}
                                  onClick={() => handleReferralPartnerChange(contact.contactId, option)}
                                >
                                  {option.label}
                                  {option.companyName && (
                                    <small className="text-muted"> - {option.companyName}</small>
                                  )}
                                </button>
                              </li>
                            ))}
                            {filteredEditOptions.length === 0 && (
                              <li className="text-muted small p-2">No matches found</li>
                            )}
                          </ul>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-secondary p-0 mt-1"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className={classes.referralCell}>
                        {savingIds.has(contact.contactId) ? (
                          <span className="text-muted">
                            <span className="spinner-border spinner-border-sm me-1" role="status" />
                            Saving...
                          </span>
                        ) : (
                          <>
                            <span>
                              {contact.referredBy}
                              {contact.referredByCompany && (
                                <small className="d-block text-muted">{contact.referredByCompany}</small>
                              )}
                            </span>
                            <button
                              type="button"
                              className={`btn btn-sm btn-link p-0 ${classes.editButton}`}
                              onClick={() => handleEditClick(contact.contactId)}
                              title="Change referral partner"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                                <path d="m15 5 4 4" />
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  <td>{contact.email || '-'}</td>
                  <td>{contact.phone || '-'}</td>
                  <td>{formatDate(contact.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const LeadsWithReferralsMemo = memo(LeadsWithReferralsComponent, areEqual);

export { LeadsWithReferralsMemo as LeadsWithReferrals };
